/**
 * Predefined LINE base-theme colour sets for the /line previews.
 *
 * A real LINE custom theme starts from one of LINE's PREDEFINED base themes,
 * which fix the chat surfaces, the incoming/outgoing bubble colours and the
 * text, the creator's own images (wallpaper, menu background, etc.) sit on top.
 * So the preview's bubble + background colours come from the chosen base set,
 * NOT from our designTokens. Each theme can be matched to a different set, so
 * this is per-theme (keyed by product key).
 */
export interface LineColorSet {
  /** App / friends-list background. */
  bg: string;
  /** Top nav, header, input bar, tab bar base. */
  surface: string;
  /** Incoming chat bubble. */
  incoming: string;
  /** Outgoing chat bubble. */
  outgoing: string;
  /** Text on the outgoing bubble. */
  outgoingText: string;
  /** Text on the INCOMING bubble; defaults to `fg` when omitted.
   *  Only needed where `fg` does not read on the incoming bubble. Every skin up
   *  to Roraima had a dark `fg` over a light incoming bubble, so `fg` happened
   *  to work and this slot did not exist — Roraima is the first with a WHITE
   *  `fg` AND a near-white incoming bubble, which put white text on white
   *  (owner 2026-08-13: "soldaki yazı balonunun içindeki yazı rengi bozuk"). */
  incomingText?: string;
  /** Primary text. */
  fg: string;
  /** Secondary / muted text. */
  fgDim: string;
  /** Text on the (coloured) header block; defaults to `fg` when omitted. */
  headerText?: string;
}

// A neutral dark base (grey bubbles), matching the reference screenshots.
const DARK_GREY: LineColorSet = {
  bg: '#17181a',
  surface: '#202126',
  incoming: '#3b3b41',
  outgoing: '#84847f',
  outgoingText: '#ffffff',
  fg: '#ffffff',
  fgDim: '#9b9ba0',
};

// The light mirror of DARK_GREY: white / off-white surfaces and bubbles with
// dark text, LINE's light base theme. Light-appearance themes preview on this.
const LIGHT_GREY: LineColorSet = {
  bg: '#ffffff',
  surface: '#f5f6f8',
  incoming: '#eef0f3',
  outgoing: '#dadde2',
  outgoingText: '#15181c',
  fg: '#15181c',
  fgDim: '#8a9096',
};

// Aurora: like Everest, the owner picked one of LINE's OWN skins rather than a
// theme-tinted one (2026-08-12) — the near-black one. So the chrome is neutral
// dark and the theme speaks through its images. This is close to DARK_GREY but
// pinned per-theme and pixel-sampled from the owner's screenshots, because
// DARK_GREY is the fallback every other dark theme still previews on and must
// not drift with one theme's skin choice.
const AURORA: LineColorSet = {
  bg: '#131313',        // body / friends list / header / passcode: near-black
  surface: '#131313',
  incoming: '#222222',  // incoming bubble: one step up from the body
  outgoing: '#4F4F4F',  // outgoing bubble: mid grey
  outgoingText: '#CFCFCF',
  fg: '#FFFFFF',
  fgDim: '#4F4F4F',
  headerText: '#FFFFFF',
};

// Sequoia: one of LINE's own stock skins, the GREEN one (owner 2026-08-13 —
// after briefly picking a brown one, and after this file first guessed at a dark
// forest green of its own). It is a LIGHT skin: a green header over a warm
// off-white body. Every hex is pixel-sampled from the owner's four screenshots.
const SEQUOIA: LineColorSet = {
  bg: '#F9F9EC',        // body / friends list: warm off-white
  surface: '#22A255',   // header / nav: LINE's green
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#2CBE67',  // outgoing bubble: the brighter green
  outgoingText: '#EBEBDE',
  fg: '#414540',        // primary text: warm near-black
  fgDim: '#94BD99',     // secondary text: soft green
  headerText: '#FFFFFF',
};

// Autumn: the owner's chosen LINE skin, every value pixel-sampled from their
// device screenshots (2026-08-13) — this replaces the provisional oranges the
// first pass guessed at. A warm apricot header over a cream body, white
// incoming bubbles and an amber outgoing bubble with DARK text on it, which is
// the one place this skin differs from the family's usual light-on-accent.
const AUTUMN: LineColorSet = {
  bg: '#FFF8EE',        // body / friends list: warm cream (sampled)
  surface: '#FEE6C1',   // header / nav: apricot (sampled)
  incoming: '#FFFFFF',  // incoming bubble: white (sampled)
  outgoing: '#FCB14C',  // outgoing bubble: amber (sampled)
  outgoingText: '#43332E',   // dark text on the amber bubble
  fg: '#43332E',        // primary text: warm near-black
  fgDim: '#C2A78F',     // secondary text / timestamps: pale tan (sampled)
  headerText: '#7D4C34', // header icons + name: brown, NOT white (sampled)
};

// Sakura: LINE's light base rendered in the theme's cherry pink — a pink header
// (white text) over a white body, soft-pink bubbles (owner 2026-07-29: previews
// mirror LINE's mockup, theme-coloured).
// All values pixel-sampled from the owner's iOS 26 mockup (Screen Shot …16.16.48).
const SAKURA: LineColorSet = {
  bg: '#FFF8FA',        // body: near-white pale pink
  surface: '#FEA5B9',   // header / nav: exact pink (owner sample)
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#FEA5B9',  // outgoing bubble: the header pink
  outgoingText: '#343434',
  fg: '#343434',        // primary text: dark charcoal
  fgDim: '#FEA5B9',     // accent / sub-text: the theme pink
  headerText: '#FFFFFF',
};

// Tokyo: the Sakura approach in the theme's plum/mauve — a dark-plum header
// (white text) over a soft lavender body, white incoming bubbles and dark-plum
// outgoing bubbles (header + outgoing share the signature plum, like Sakura's
// pink). All values pixel-sampled from the owner's LINE mockup (Downloads/
// Screenshot 2026-08-05 at 12.56.44) so the previews mirror LINE's mandated base.
const TOKYO: LineColorSet = {
  bg: '#B99EC5',        // body: soft lavender-mauve
  surface: '#51385E',   // header / nav: dark plum
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#51385E',  // outgoing bubble: the signature plum
  outgoingText: '#FFFFFF',
  fg: '#51385E',        // primary text: dark plum
  fgDim: '#886399',     // secondary / muted text: muted mauve (sampled)
  headerText: '#FFFFFF',
};

// Everest: unlike Sakura/Tokyo, which paint the chrome in the theme's own hue,
// the owner picked one of LINE's stock skins in the Creators Market Color Skins
// tab (2026-08-12, second pick — a white-chrome skin replacing the azure-banded
// one). Everest shows up through its IMAGES (wallpaper, tab glyphs, menu strip,
// summit passcode dots) over LINE's own colours. Every hex is pixel-sampled from
// the owner's four screenshots of that skin, so the previews render what the
// phone actually draws rather than a theme-tinted wish.
const EVEREST: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list: white
  surface: '#FFFFFF',   // header / nav / tab bar: white, not a coloured band
  incoming: '#FFFFFF',  // incoming bubble: white (LINE hairlines it, see --incoming-border)
  outgoing: '#AFCFE7',  // outgoing bubble: the skin's soft blue
  outgoingText: '#4C6A81',
  fg: '#42637D',        // primary text: slate blue
  fgDim: '#9CC4E2',     // secondary / muted text: pale blue
  headerText: '#1469B2',// header ink is BLUE ON WHITE here, not white on colour
};

// Roraima: the owner's chosen LINE skin, every value pixel-sampled from their
// four device screenshots (2026-08-13, ~/Documents/Temp/Screenshots). This
// REPLACES the terracotta-on-sand set the first pass derived from the theme's
// palette, which was wrong in the most basic way: the skin is DARK, a single
// warm brown carrying the header, the body, the status bar and the talk-room
// wallpaper alike, with no coloured header band at all.
//
// Its one loud note is the outgoing bubble, which is LINE's own bright yellow
// with dark ink on it — and it happens to land on the theme's own Dry Grass
// family, which is why the tumbleweed dots and the yellow profile figures the
// owner asked for sit so naturally on this skin.
const RORAIMA: LineColorSet = {
  bg: '#5C3E30',        // body / friends list / status bar: one warm dark brown
  surface: '#5C3E30',   // header / nav: the same brown, not a band
  incoming: '#FCECE7',  // incoming bubble: warm near-white
  outgoing: '#FFD939',  // outgoing bubble: the skin's bright yellow
  outgoingText: '#403710',   // dark olive ink on the yellow
  incomingText: '#1C1A19',   // near-black ink on the near-white bubble (sampled)
  fg: '#FFFFFF',        // primary text
  fgDim: '#9A877F',     // secondary text / timestamps
  headerText: '#FFFFFF',
};

// Miami: the owner's chosen skin, every hex pixel-sampled from their four device
// screenshots (2026-08-13). A LIGHT skin, and the loudest in the family so far: a
// hot pink header over a white body, with the pink carried through the search
// field, the input bar, the passcode and every timestamp.
//
// Its bubbles are the quiet part. Both are near-white with dark text — the
// outgoing one only a shade of pink away from the incoming — so the chat reads
// calm even though the chrome shouts.
const MIAMI: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list: white
  surface: '#FA5392',   // header / nav: hot pink
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#FFE9F1',  // outgoing bubble: the palest pink
  outgoingText: '#5E5252',   // dark ink on it, not white
  fg: '#3F3734',        // primary text: warm near-black
  fgDim: '#FB73A6',     // secondary text / timestamps: the skin's pink
  headerText: '#FFFFFF',
};

// Istanbul: the owner's chosen skin, every hex pixel-sampled from their four
// device screenshots (2026-08-14). A LIGHT skin, and the most single-minded one
// in the family: one bright cyan carries the header, the chat input bar AND the
// passcode's upper zone alike, while the body under all of it is a near-white
// pale cyan — the same colour at two strengths rather than a colour and a
// neutral.
//
// Its outgoing bubble is where it parts from the usual light-on-accent: a MID
// cyan with the dark slate-teal ink on it, not white, because the body is itself
// already tinted and a white-on-accent bubble would be the only cold-white
// surface on the screen besides the incoming bubble.
const ISTANBUL: LineColorSet = {
  bg: '#F2FDFF',        // body / friends list / chat body: very pale cyan
  surface: '#4AC3E0',   // header / nav / input bar: the bright cyan
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#88DBEE',  // outgoing bubble: mid cyan
  outgoingText: '#033A51',   // dark slate-teal ink on it, not white
  fg: '#033A51',        // primary text: dark slate-teal
  fgDim: '#6AC2CE',     // secondary text / timestamps: soft cyan
  headerText: '#FFFFFF',
};

// Paris: the owner's chosen skin, every hex pixel-sampled from their four device
// screenshots (2026-08-14 19.22-19.23). A PINK skin, and the third in the family
// after Sakura and Miami — but its own: where Sakura is a soft blossom pink and
// Miami a hot neon one, this is a warm rose over a near-white body, with the
// pink carried into the bubbles rather than kept in the chrome.
//
// Two things about it are unusual enough to be worth recording.
//
// Its INPUT BAR is a paler pink than its header (#FDBCC8 against #FE7898), which
// almost no skin in this family does — and it matters beyond looks, because the
// classic tab strip has to end on the input bar, not the header (see TAB_FILL).
//
// And the device draws the friends-list sub-lines in a CYAN accent, #4FD0F1,
// while "See more" and the row counts are pink. One `fgDim` slot cannot be both.
// The pink is taken because it covers the sub-text this preview actually draws
// most of — the counts, the links and the timestamps — and a cyan timestamp on a
// rose theme would read as a bug rather than as a skin.
const PARIS: LineColorSet = {
  bg: '#FFF5F5',        // body / friends list: near-white pink (chat body #FFF1F2)
  surface: '#FE7898',   // header / nav: the warm rose
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#FEBECD',  // outgoing bubble: mid pink
  outgoingText: '#382E26',   // dark warm ink on it, not white
  fg: '#382E26',        // primary text: a dark warm near-black
  fgDim: '#FE97B0',     // secondary text / timestamps: the skin's own softer pink
  headerText: '#FFFFFF',
};

// Rio de Janeiro: the owner's chosen skin, every hex pixel-sampled from their
// four device screenshots (2026-08-14 21.23). It is the owner's SECOND pick — the
// first was LINE's stock green, and it went back the moment they saw it next to
// Sequoia, which sits on exactly that skin ("aa sequoia ile aynı olmuş").
//
// This one is NEUTRAL: a light grey nav band with near-BLACK ink over a white
// body, and grey again on the talk room. The green appears in three places only —
// the outgoing bubble, the chat input bar and the passcode keypad — so it reads
// as an accent on a white app rather than as a green app.
//
// Everest is the only other skin in this family shaped like this, and the same
// consequence follows: the theme has to speak almost entirely through its IMAGES,
// because the chrome carries nearly no colour of its own.
const RIO: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list: white (sampled)
  surface: '#F6F6F6',   // header / nav band: light grey, NOT a coloured band
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#27D07C',  // outgoing bubble: the skin's bright green
  outgoingText: '#FFFFFF',
  fg: '#121212',        // primary text: near-black (sampled #020202 at its core)
  fgDim: '#B7B7B7',     // secondary text / timestamps: light grey
  headerText: '#121212',// header ink is BLACK ON GREY here, not white on colour
};

// Tallinn: the owner's chosen skin, every hex pixel-sampled from their four
// device screenshots (2026-08-14 22.06). It REPLACES Istanbul's set, which stood
// in here for an hour while the assets were drawn.
//
// It is a two-part skin, and that split is the thing to know about it: the CHROME
// is a strong azure — header, nav, the passcode keypad — while everything the
// content sits on is white or a pale blue. So the theme's own images have a light
// ground everywhere except the top band.
//
// Its outgoing bubble is LINE's raw yellow with dark ink on it, the loudest note
// in the skin and the only warm one; the roofs on this theme's menu strip are the
// only other warmth it has.
const TALLINN: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list: white (sampled)
  surface: '#0D8DC3',   // header / nav: the azure (sampled)
  incoming: '#FFFFFF',  // incoming bubble: white
  outgoing: '#FFFF2A',  // outgoing bubble: LINE's yellow (sampled)
  outgoingText: '#3C3C3C',   // dark grey ink on the yellow, not white
  fg: '#3C3C3C',        // primary text: dark grey
  fgDim: '#7AC6E1',     // secondary text / sub-lines: the skin's light blue
  headerText: '#FFFFFF',
};

// Ocean: PIXEL-SAMPLED from the owner's four device screenshots (2026-08-15
// 19.15/19.16 — home, empty chat, chat with bubbles, passcode).
//
// The sample OVERTURNED this theme's whole preview. Every value here used to be
// derived, and what was derived was a DEEP-SEA skin: near-black water on the
// body, a step lighter on the chrome, bioluminescence carrying the images. The
// skin the owner actually picked is the opposite — a BRIGHT teal header over a
// near-white talk room, LINE's own light shape.
//
// That is not a tint change, and nothing here should be read as one. It flips
// the polarity of every mark this theme draws: the wallpaper's jellyfish, the
// passcode's four creatures and the strip's seabed were all pale glows aimed at
// deep water, and against #EDFAF9 a pale glow is nothing at all. They are dark
// marks on lit water now. See buildOcean in line-chat-bg / line-menu-bg and
// JELLY_PC_COLORS.
const OCEAN: LineColorSet = {
  bg: '#EDFAF9',        // body / friends list / talk room: the palest sea-mist (sampled)
  surface: '#2FB8A7',   // header / nav / input bar: the skin's bright teal (sampled)
  incoming: '#FFFFFF',  // incoming bubble: white (sampled)
  outgoing: '#6FD1C5',  // outgoing bubble: the skin's own mid teal (sampled)
  outgoingText: '#102B2E',   // the theme's own abyss as ink, 8.25:1 on it
  fg: '#102B2E',        // primary text: 13.96:1 on the body, 14.93 on white
  fgDim: '#4E7A78',     // secondary text / timestamps: the slate the shots draw, 4.49:1
  headerText: '#FFFFFF',     // what the device draws on the teal band (2.46:1, LINE's own call)
};

// Salda: PIXEL-SAMPLED from the owner's four device screenshots (2026-08-15
// 21.24 — home, empty chat, chat with bubbles, passcode). The phone was still
// carrying Autumn's images at the time, so only the CHROME is sampled here; the
// maple leaves in those shots are not this theme.
//
// The derived guess had the SHAPE right and every value wrong. It reasoned that
// a sand-dominant palette wants a warm off-white body under a lone turquoise
// header, and that is what the owner picked — but a softer, greener turquoise, a
// warmer cream, and one thing no derivation would have reached: the input bar is
// SALMON. Not the header's turquoise and not the pale sand this file predicted.
//
// That salmon is the load-bearing surprise, because the classic tab strip has to
// end on the input bar (see TAB_FILL). The whole point of the derived pale sand
// was to let the strip's beach run into the bar with no edge; the beach now runs
// into a warm coral band instead, and the strip's sand had to be re-tuned toward
// it rather than away.
//
// The BUBBLES are the other thing worth stating: both are WHITE, and the skin
// separates them with a hairline outline — pale green on the incoming, pale pink
// on the outgoing. LineColorSet carries fills only, so the preview draws two
// white bubbles, which is what the phone draws too, minus a one-pixel edge.
const SALDA: LineColorSet = {
  bg: '#FFF8E8',        // body / friends list / talk room: the skin's cream (sampled)
  surface: '#70CACE',   // header / nav: the skin's soft turquoise (sampled)
  incoming: '#FFFFFF',  // incoming bubble: white, hairlined #E5F3C9 (sampled)
  outgoing: '#FFFFFF',  // outgoing bubble: white too, hairlined #FCE4E1 (sampled)
  outgoingText: '#312408',   // the theme's own dark umber on it, 14.44:1
  fg: '#312408',        // primary text: the theme's own dark umber
  fgDim: '#A59379',     // secondary text / timestamps: the shore's sandstone
  headerText: '#FFFFFF',
};

// Prague: PIXEL-SAMPLED from the owner's four device screenshots (2026-08-15
// 22.17), and the sample landed somewhere no derivation would have gone: this is
// LINE's own NEAR-BLACK stock skin, hex for hex the same one Aurora uses.
//
// The derived set built a navy — a night that is plainly blue, on the argument
// that the theme's icon is a gold-lit castle against a blue sky and the blue is
// half the picture. That argument is still true of the THEME; it is just not what
// the owner picked for the phone. They chose the skin that gets out of the way,
// the same call they made on Aurora and Everest, and the reasoning holds better
// here than the navy did: a scene about lamps coming on wants the dark around
// them to be nothing at all.
//
// It is deliberately NOT written as `AURORA` reused. Two themes agreeing today is
// not the same as one value: either could be re-skinned on its own, and a shared
// constant would silently drag the other with it.
const PRAGUE: LineColorSet = {
  bg: '#131313',        // body / friends list / talk room / passcode: near-black
  surface: '#131313',   // header / nav / input bar: the same near-black, no band
  incoming: '#222222',  // incoming bubble: one step up from the body
  outgoing: '#4F4F4F',  // outgoing bubble: mid grey
  outgoingText: '#FFFFFF',   // 8.32:1 on it
  fg: '#FFFFFF',        // primary text
  fgDim: '#7C93AD',     // secondary text / timestamps: the theme's own slate
  headerText: '#FFFFFF',
};

// Sydney: PIXEL-SAMPLED from the owner's four device screenshots (2026-08-15
// 23.41).
//
// The derived guess got the HEADER almost exactly right — #0F91C7 against the
// sampled #0D8DC3 — and then missed the two things that actually characterise
// this skin, both of which are the kind no palette reasoning reaches.
//
// The OUTGOING BUBBLE IS YELLOW. Pure, bright, #FFFF2A, on a theme whose whole
// palette is harbour blue and sandstone. The derived pale-harbour bubble was
// defensible and simply not what the phone draws.
//
// It is the SAME yellow Tallinn's sampled skin carries, which is the useful part:
// two owner-picked skins now share it, so it is evidently a LINE stock bubble
// rather than a per-theme choice, and the next derived guess should reach for it
// before reaching for the theme's own accent. It is left written out here rather
// than shared with TALLINN — two skins agreeing today is not one value, and
// either could be re-picked alone.
//
// The rest of the chrome is NEUTRAL: white body, grey search field, grey input
// bar, grey glass pill. Only the header and the passcode keypad carry the blue.
// The derived set tinted everything toward the harbour, which is the instinct a
// theme's own palette gives you and the one these four screenshots keep
// correcting.
const SYDNEY: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list: white (sampled)
  surface: '#0D8DC3',   // header / nav / passcode keypad: the harbour blue (sampled)
  incoming: '#FFFFFF',  // incoming bubble: white (sampled)
  outgoing: '#FFFF2A',  // outgoing bubble: YELLOW (sampled)
  outgoingText: '#1C1C1C',   // near-black on it, 15.90:1
  fg: '#1C1C1C',        // primary text: LINE's own near-black, not a tinted teal
  fgDim: '#8A8A8A',     // secondary text / timestamps: neutral grey (sampled)
  headerText: '#FFFFFF',     // 3.74:1 on the blue band
};

// Reynisfjara: PIXEL-SAMPLED from the owner's four device screenshots
// (2026-08-16). It is LINE's own NEAR-BLACK stock skin — the third theme to land
// on it, after Aurora and Prague, hex for hex.
//
// The derived set built a cool Nordic dark of its own: #20262C on the chrome,
// #151A20 on the body, a slate-blue cast through all of it. That is the theme's
// palette talking, and it is not what the owner picked. They chose the skin that
// gets out of the way — the same call they have now made on every dark theme in
// the run — and on a beach photographed at dusk it is the better one: the images
// carry all the colour there is, and neutral black is the only ground that does
// not tint them.
//
// Written out rather than shared with AURORA or PRAGUE. Three themes agreeing
// today is still three choices, and any of them could be re-skinned alone.
const REYNISFJARA: LineColorSet = {
  bg: '#131313',        // body / friends list / talk room / passcode: near-black
  surface: '#131313',   // header / nav / input bar: the same near-black, no band
  incoming: '#222222',  // incoming bubble: one step up from the body
  outgoing: '#4F4F4F',  // outgoing bubble: mid grey
  outgoingText: '#FFFFFF',
  fg: '#FFFFFF',        // primary text
  fgDim: '#8B95A2',     // secondary text / timestamps: the theme's own slate
  headerText: '#FFFFFF',
};

// Spring: PIXEL-SAMPLED from the owner's four device screenshots (2026-08-16).
//
// A WHITE skin with green accents, and the derived set had it the other way
// round: it built a green header over a pale-meadow body, which is what the
// theme's palette suggests and not what the owner picked. Here the body, the
// header and the talk room are all plain white; the green appears only in the
// search field, the input pill and the passcode keypad, and the header's own ink.
//
// The OUTGOING BUBBLE IS YELLOW — the third sampled skin in the run to carry one,
// after Tallinn and Sydney. Theirs is LINE's hard #FFFF2A; this is a softer
// #FFE772, so the yellow is not a single stock value but a family of them, and
// on the next derived guess a yellow of SOME kind is the likeliest answer for an
// outgoing bubble whatever the theme's own accent is.
const SPRING: LineColorSet = {
  bg: '#FFFFFF',        // body / friends list / talk room: white (sampled)
  surface: '#FFFFFF',   // header / nav / input bar: white too, no coloured band
  incoming: '#EDEDED',  // incoming bubble: LINE's grey (sampled)
  outgoing: '#FFE772',  // outgoing bubble: a soft yellow (sampled)
  outgoingText: '#2B2B2B',   // near-black on it, 12.55:1
  fg: '#2B2B2B',        // primary text
  fgDim: '#9AA69B',     // secondary text / timestamps
  headerText: '#91C23D',// the header's ink is GREEN ON WHITE, not white on colour
};

// Vienna: PIXEL-SAMPLED from the owner's device screenshots (2026-08-16), not
// derived. The theme is a dark wine one and every value here had been reasoned
// out from that — a #2C1D1D body under a #4B2226 header — and the skin they
// actually picked is the exact opposite: LINE's vermilion chrome over a warm
// blush room.
//
// That is the sixth skin sampled in this family and the sixth that did not match
// what had been derived for it. The lesson has stopped being a surprise: a theme's
// palette says what its EDITOR looks like, and says nothing about which LINE skin
// its owner will want to chat on.
//
// The inversion is not cosmetic. Vienna's passcode ramp ran 2.77 / 4.38 / 7.44 /
// 10.61 up the old dark zone and runs 5.06 / 3.20 / 1.89 / 1.32 down this pale
// one, and the unentered dot went from 1.40 to 10.00 — the empty slot would have
// been the loudest mark on the lock screen. Everything keyed to this set had to
// be re-solved, not re-tinted.
const VIENNA: LineColorSet = {
  bg: '#F8EDE7',        // body / friends list / talk room: the warm blush
  surface: '#EE4333',   // header / nav: LINE's vermilion
  incoming: '#FFFFFF',  // incoming bubble: white, 1.15 on the room
  outgoing: '#ECD3C3',  // outgoing bubble: dusty tan, 1.24 on the room
  outgoingText: '#3A2422',   // 9.86:1 on it — the tan is far too pale for white
  fg: '#3A2422',        // primary text: a near-black warmed toward the theme
  fgDim: '#8A7168',     // secondary text
  headerText: '#FFFFFF',     // 3.83:1 on the vermilion, which is LINE's own choice
};

// Madrid: PIXEL-SAMPLED from the owner's device screenshots (2026-08-16).
//
// This replaces a flamenco-red header over a cream body, which was a reasonable
// guess and the seventh guess in a row to be wrong.
//
// What makes this skin unlike every other one here: it is ONE COLOUR. The header,
// the friends list, the talk room and the passcode keypad are all #FFDD66, with no
// step between them at all. Roraima is the only other skin in the family built
// that way and it is a dark brown; this is a saturated saffron, which is a much
// harder ground to put anything on.
//
// It also inverts the family's usual ink. Every other light skin here puts white
// or near-white text on a coloured header; this one runs a near-black warm brown
// at 9.93:1 on the yellow, because white on #FFDD66 measures 1.33 and is simply
// not readable. Nothing about this skin can be derived from the last one.
const MADRID: LineColorSet = {
  bg: '#FFDD66',        // body / friends list / talk room / header: ONE saffron
  surface: '#FFDD66',   // the header is not a separate surface on this skin
  incoming: '#FFFFFF',  // incoming bubble: white, 1.33 on the saffron
  outgoing: '#FFF561',  // outgoing bubble: a brighter, greener yellow — 1.17
  outgoingText: '#3A2F08',   // 9.30:1 on it; white would be 1.13
  fg: '#3A2F08',        // primary text: the warm near-black, 9.93 on the ground
  fgDim: '#8C7A3A',     // secondary text / timestamps
  headerText: '#3A2F08',// header ink is DARK here, not white
};

// Etna: PIXEL-SAMPLED from the owner's device screenshots (2026-08-17).
//
// The CLOSEST any sample has come to what was derived for it, and still not the
// same. The guess was right about the register — Etna's palette is basalt and fire,
// and a skin that lifted the greys to be comfortable would throw away the half of
// the theme that is a mountain at night — and it was wrong about the one thing that
// mattered: the outgoing bubble.
//
// The derived version made that bubble the theme's EMBER (#C0522F) on the grounds
// that it was the only hot thing the chrome could carry. The owner picked LINE's
// stock near-black, where the outgoing bubble is a neutral grey. So the chrome has
// no fire in it at all now, and every ember in this theme lives in an image — which
// is the same shape Aurora, Prague and Reynisfjara all take on this identical skin.
const ETNA: LineColorSet = {
  bg: '#131313',        // body / friends list / talk room / header: near-black
  surface: '#131313',   // the header is not a separate surface on this skin
  incoming: '#222222',  // incoming bubble: one step up from the body
  outgoing: '#4F4F4F',  // outgoing bubble: mid grey — NOT the theme's ember
  outgoingText: '#CFCFCF',
  fg: '#FFFFFF',
  fgDim: '#4F4F4F',
  headerText: '#FFFFFF',
};

// Fuji: PIXEL-SAMPLED from the owner's device screenshots (2026-08-17). The LAST of
// the 22 to be sampled.
//
// The register was right and every value was off. The guess reasoned that Fuji's
// palette is a DAWN, that the skin should take the two quietest moments of it and
// leave the rest to the images — and that reasoning survives. What it got wrong is
// how warm and how PINK the owner would go: a near-white body became a blush, and
// the dawn rose on the header became a brighter coral than anything derived from
// this palette would have suggested.
//
// It is also the loudest LIGHT skin here after Miami's, and it makes the same choice
// Miami does: the header colour runs on through the passcode keypad, so the pink is
// not a band at the top but the frame around everything.
const FUJI: LineColorSet = {
  bg: '#FFE8E1',        // body / friends list / talk room: the blush
  surface: '#FE939C',   // header / nav: LINE's coral
  incoming: '#FFFFFF',  // incoming bubble: white, 1.17 on the room
  outgoing: '#FEC5C7',  // outgoing bubble: a deeper coral, 1.27
  outgoingText: '#8C5358',   // 4.86:1 on it — white would be 1.85
  fg: '#45302B',        // primary text: the theme's own dark umber, 10.45 on the room
  fgDim: '#A67274',     // secondary text / timestamps (sampled)
  headerText: '#FFFFFF',// 2.13:1 on the coral, which is LINE's own choice
};

// Per-theme assignment. Add a theme key here to give it a different base set.
const SETS: { [ key: string ]: LineColorSet } = {
  'aurora-borealis': AURORA,
  'sakura': SAKURA,
  'tokyo': TOKYO,
  'everest': EVEREST,
  'sequoia': SEQUOIA,
  'autumn': AUTUMN,
  'roraima': RORAIMA,
  'miami': MIAMI,
  'istanbul': ISTANBUL,
  'paris': PARIS,
  'rio-de-janeiro': RIO,
  'tallinn': TALLINN,
  'ocean': OCEAN,
  'salda': SALDA,
  'prague': PRAGUE,
  'sydney': SYDNEY,
  'reynisfjara': REYNISFJARA,
  'spring': SPRING,
  'vienna': VIENNA,
  'madrid': MADRID,
  'etna': ETNA,
  'fuji': FUJI,
};

/** The base set for a theme: its per-key override if one is pinned, otherwise
 *  the light or dark neutral set matching the theme's own appearance. Used by the
 *  LINE previews (line-home / line-card / line-thumbnail), which DO want the
 *  theme-tinted chat colours. */
export function lineColorSet( key: string | undefined, appearance?: string ): LineColorSet {
  if ( key && SETS[ key ] ) {
    return SETS[ key ];
  }
  return appearance && appearance.startsWith( 'Light' ) ? LIGHT_GREY : DARK_GREY;
}

/**
 * The fill LINE paints on the CLASSIC tab bar — the surface the menu background
 * (asset i_11, 1472×150) is laid on, and therefore the colour of everything
 * around and below that strip. A strip that does not END on this colour shows a
 * visible edge on the phone, which is exactly the bug that keeps coming back
 * (owner 2026-08-13: "bunu hep unutuyorsun").
 *
 * It lives here because it is needed in two places that must never disagree:
 * line-home paints the preview's tab bar with it, and every menu-bg design has
 * to land its bottom stop on it. Two hand-maintained copies is what let them
 * drift; one function cannot.
 *
 * Evidence for the values: on Sakura, Tokyo, Everest and Aurora this fill is
 * EXACTLY the chat screen's bottom input bar (line-card's `inputBg`), every
 * time — so a theme with no device screenshot of the classic bar takes its
 * input bar. Autumn is the first skin whose header and input bar differ, so it
 * is the first where that rule actually decides something.
 */
const TAB_FILL: { [ key: string ]: string } = {
  'sakura': '#FFE0E7',
  'tokyo': '#886399',              // == its chat input bar
  'everest': '#FFFFFF',            // LINE default skin: white tab bar
  'aurora-borealis': '#131313',    // == its chat input bar
  'sequoia': '#198444',            // == its chat input bar
  'autumn': '#C7813B',             // == its chat input bar (caramel)
  'roraima': '#422C21',            // == its chat input bar (sampled)
  'miami': '#FECDE0',              // == its chat input bar (sampled)
  'istanbul': '#4AC3E0',           // == its chat input bar (sampled)
  // PLACEHOLDER: Roraima's tab fill, verbatim. The owner has not chosen Paris's
  // LINE skin yet — resample this from device screenshots (it must equal the
  // chat input bar) once they do.
  'paris': '#FDBCC8',           // == its chat input bar (sampled), NOT the header
  // The owner's own value (2026-08-14), not a sample: #119B44 is what the
  // scaled-down screenshots render this green as, and the four device shots
  // could not settle it anyway — that phone was showing the iOS-26 floating bar,
  // not the classic strip.
  'rio-de-janeiro': '#00A64B',     // == its chat input bar, the skin's green
  // Sampled: this skin's chat input bar is a near-WHITE, not the azure of its
  // header — the same shape Everest has. So the strip ends on snow, which is the
  // one piece of luck in this theme's whole build.
  'tallinn': '#F9F9F9',
  // Ocean: == its chat input bar, SAMPLED — and the sample moved it a long way.
  // The derived value was a deep teal #123A40; the phone draws the input bar in
  // the header's own bright teal, the shape Rio has. The strip's own bottom stop
  // reads from here, so the seabed now has to ramp into a LIT band rather than
  // into deep water — which is why buildOcean's whole water gradient had to be
  // rebuilt as shallows.
  'ocean': '#2FB8A7',
  // Salda: == its chat input bar, SAMPLED — and the sample overturned the one
  // idea this entry was built on. The derived value was PALE SAND (#FBF3E3) on
  // the reasoning that a beach scene should run into a bar of the same material.
  // The phone draws SALMON. So the strip's sand now meets a warm coral band, and
  // the beach's own lower stop had to be pulled toward it or the strip ends on a
  // visible seam.
  'salda': '#F5B6A6',
  // Prague: == its chat input bar, SAMPLED. The derived value was the navy
  // #123151, and the whole design idea attached to it was that the strip's RIVER
  // ends on that exact blue so the water of the scene and the fill of the bar are
  // one surface. The phone draws near-black, so the river now runs into nothing —
  // which, for a scene about lamps over a dark river at night, is the better
  // ending anyway.
  'prague': '#131313',
  // Sydney: == its chat input bar, SAMPLED. The derived value was the pale
  // harbour #CAE4EC, chosen so the strip's own water could run into the tab bar
  // with no edge. The phone draws a NEUTRAL near-white there — this skin keeps
  // its blue for the header and the keypad and leaves everything else grey — so
  // the strip now ends on light rather than on water.
  // Sydney: == its chat input bar, derived. The PALE HARBOUR, not the header's
  // blue — so the strip's own water runs into the tab bar with no edge, the same
  // trick Salda's beach and Prague's river play. Three scenes in this run end on
  // water at the bottom, and that is not a coincidence: it is the easiest thing
  // in a landscape to hand off to a flat fill.
  'sydney': '#F9F9F9',
  // Reynisfjara: == its chat input bar, SAMPLED. The derived value was the
  // theme's own #20262C; the phone draws LINE's near-black, so the strip's beach
  // now runs into nothing rather than into a cool slate — which for a black sand
  // beach at dusk is the ending it wanted anyway.
  // Reynisfjara: == its chat input bar, derived. The strip's BLACK SAND ends on
  // this exact grey, so the beach and the bar are one surface — the fourth scene
  // in this run to hand its foreground off to the fill, after three that did it
  // with water.
  'reynisfjara': '#131313',
  // Spring: == its chat input bar, SAMPLED. The derived value was a pale meadow
  // green; the phone draws WHITE there and keeps its green for the pill inside the
  // bar. So the strip's own meadow now ends on white rather than running into more
  // green — which costs this scene the seamless hand-off its haze was built for,
  // and is simply what the skin does.
  // Spring: == its chat input bar, derived. A PALE meadow rather than the header's
  // green, so the strip's own foreground grass runs into the bar with no edge —
  // the same handoff Salda's beach, Prague's river and Sydney's harbour make.
  'spring': '#FFFFFF',
  // Vienna: == its chat INPUT BAR, sampled (#FAF4EF). The strip is a sheet of
  // music now and the paper it is printed on IS the tab fill, so the staff runs
  // straight off the bottom of the image onto the bar with nothing between them.
  // The scene this replaced was the inside of the opera house, which ended on
  // wine-red velvet — furniture handing off to the bar, and impossible to keep
  // once the sampled bar turned out to be near-white.
  'vienna': '#FAF4EF',
  // Madrid: SAMPLED — the tab bar's own capsule, a pale saffron one step lighter
  // than the body it floats on. The strip's street ends on this, so the pavement
  // runs into the bar with no edge, which is the rule every scene here follows.
  'madrid': '#FFE897',
  // Etna: SAMPLED — the tab bar's own strip, one step up from the near-black body.
  // The strip's LAVA FIELD ends on this, so the ground runs into the bar with no
  // edge, which is the rule every scene here follows.
  'etna': '#1C1C1C',
  // Fuji: SAMPLED — the tab bar's own strip, a shade warmer than the blush body it
  // sits on. The strip's MIST ends on this, so the haze runs into the bar with no
  // edge, which is the rule every scene here follows.
  'fuji': '#FFF0EA',
};

export function lineTabFill( key: string | undefined, appearance?: string ): string {
  if ( key && TAB_FILL[ key ] ) {
    return TAB_FILL[ key ];
  }
  return lineColorSet( key, appearance ).bg || 'transparent';
}

/** The scrim/panel set for a POSTER card (themes page + pinterest), chosen by
 *  APPEARANCE ONLY — deliberately NEVER a per-theme LINE override. This keeps the
 *  LINE chat colours (e.g. Tokyo's light lavender chat body) from bleeding into a
 *  dark theme's poster and washing out its title (owner 2026-08-05: the LINE data
 *  must be a separate variable from the poster's). */
export function posterColorSet( appearance?: string ): LineColorSet {
  return appearance && appearance.startsWith( 'Light' ) ? LIGHT_GREY : DARK_GREY;
}
